// Account context (contract v1.8 section 3, account-aware app). The shell
// 0.4.0 has no account switcher and never sends X-Glm-Account, so Sentinel
// keeps one here (local fallback, Massimo 2026-10-23 decision 6) until the
// shell has its own: the chosen account rides on every tenant-API call as
// X-Glm-Account, which limits the request to that account and below.
import { computed, signal } from "@preact/signals";
import { bearer, config } from "@gimasi/giothub-shell";

export interface Account {
  id: string;
  parent_id: string | null;
  kind: "root" | "partner" | "sub_partner" | "customer" | "unit" | "region" | "division" | string;
  name: string;
  ancestors?: string[];
  created_at?: string;
  closed_at?: string | null;
  login_org?: string | null;
}

const KEY = "sentinel.account";
const read = () => {
  try {
    return localStorage.getItem(KEY) || "";
  } catch {
    return "";
  }
};

export const accounts = signal<Account[]>([]);
export const accountMode = signal<string>("single");
/** "" = no header: the caller's whole account set */
export const currentAccount = signal<string>(read());
/**
 * A customer address (glm-console >= v1.27.0): config.json carries the account the host belongs
 * to, e.g. massimo.futurefridges.com -> customer "Massimo". Then every tenant-API call is limited to
 * that account's subtree, and the partner's navigation is hidden. Absent = the whole tenant.
 */
export interface BoundAccount { id: string; name: string; login_org?: string | null }
export function bound(): BoundAccount | null {
  try {
    const a = (config() as unknown as { account?: BoundAccount | null }).account;
    return a && a.id ? a : null;
  } catch {
    return null;
  }
}
const within = (id: string, root: string) => id === root || !!accounts.value.find((a) => a.id === id)?.ancestors?.includes(root);
/** the account sent as X-Glm-Account: the chosen one, held inside the host's account when there is one */
export function effectiveAccount(): string {
  const b = bound(), c = currentAccount.value;
  if (!b) return c;
  return c && within(c, b.id) ? c : b.id;
}
export const current = computed(() => accounts.value.find((a) => a.id === (currentAccount.value || bound()?.id || "")) || null);
/** more than one account visible: show the switcher and the Customer column */
export const multi = computed(() => accounts.value.length > 1);

export function setAccount(id: string) {
  currentAccount.value = id;
  try {
    if (id) localStorage.setItem(KEY, id);
    else localStorage.removeItem(KEY);
  } catch {
    /* storage blocked: keep in memory */
  }
}

/** Accounts in tree order (parent before children), with their depth below the root. */
export const tree = computed(() => {
  const kids = new Map<string | null, Account[]>();
  for (const a of accounts.value) kids.set(a.parent_id, [...(kids.get(a.parent_id) || []), a]);
  const out: { a: Account; depth: number }[] = [];
  const walk = (id: string | null, depth: number) => {
    for (const a of (kids.get(id) || []).sort((x, y) => x.name.localeCompare(y.name))) {
      out.push({ a, depth });
      walk(a.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
});

/** the root account cannot be renamed through the API: shown as the tenant's name */
export const displayName = (a: Account | undefined | null) => {
  if (!a) return "";
  if (a.parent_id) return a.name;
  try { return config().tenant_name || a.name; } catch { return a.name; }
};
export const accountName = (id: string | null | undefined) => displayName(accounts.value.find((a) => a.id === id));

/** The customer an account belongs to (itself or its nearest customer ancestor). */
export function customerOf(id: string | null | undefined): Account | null {
  const byId = new Map(accounts.value.map((a) => [a.id, a]));
  let a = id ? byId.get(id) : undefined;
  while (a) {
    if (a.kind === "customer") return a;
    a = a.parent_id ? byId.get(a.parent_id) : undefined;
  }
  return null;
}

/** Plain tenant-API call for routes the shell's client does not have yet. */
export async function tenantApi<R>(method: string, path: string, body?: unknown, account?: string): Promise<R> {
  const token = await bearer();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (account) headers["X-Glm-Account"] = account;
  if (token) headers.Authorization = "Bearer " + token;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const r = await fetch(config().api_base + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const text = await r.text();
  const data = text ? JSON.parse(text) : null;
  if (!r.ok) throw Object.assign(new Error((data && (data.message || data.code)) || r.statusText), { status: r.status, code: data?.code, field_errors: data?.field_errors || data?.fields });
  return data as R;
}

let accountsLoad: Promise<void> | null = null;
/** the account list, loaded once (the frame and the fleet both need it before they can work) */
export function accountsReady(): Promise<void> {
  // a failed load (empty list) is tried again by the next caller
  return (accountsLoad ||= loadAccounts().then(() => { if (!accounts.value.length) accountsLoad = null; }));
}

export async function loadAccounts() {
  try {
    const r = await tenantApi<{ mode: string; items: Account[] }>("GET", "/accounts");
    accountMode.value = r.mode;
    accounts.value = r.items;
    if (currentAccount.value && !r.items.some((a) => a.id === currentAccount.value)) setAccount("");
  } catch {
    accounts.value = [];
  }
}

// Every request to the tenant API carries the chosen account (one place,
// so the shell's own client gets it too). Removed when the shell does this.
let patched = false;
export function installAccountHeader() {
  if (patched) return;
  patched = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let base = "";
    try {
      base = config().api_base;
    } catch {
      /* config not loaded yet */
    }
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    // /accounts lists the switcher's choices: the whole set, or the host's account and below
    const isList = /\/accounts(\?|$)/.test(url);
    const toApi = base && (url.startsWith(base) || url.startsWith(location.origin + base));
    const acc = isList ? bound()?.id || "" : effectiveAccount();
    const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
    if (!acc || !toApi || headers.has("X-Glm-Account")) return orig(input, init);
    headers.set("X-Glm-Account", acc);
    return orig(input, { ...init, headers });
  };
}
